import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import confetti from "canvas-confetti";
import ahoy from "ahoy.js";
import { i18n } from "../lib/i18n";
import { WalkthroughSurface, type GuidedSurface } from "../lib/walkthrough_surface";

interface TourStep extends GuidedSurface {
	id: string;
	page: string;
	element?: string;
	type?: string;
	title: string;
	description: string;
	side?: string;
	help_article?: string;
	open_menu?: boolean;
	confetti?: boolean;
}

interface TourSummary {
	collection?: string;
	id: string;
	name: string;
	description: string;
	completed: boolean;
	dismissed: boolean;
}

interface TourData {
	standalone?: boolean;
	collection?: string;
	context?: string;
	demo_neighbor?: boolean;
	id: string;
	name: string;
	steps: TourStep[];
	state: { completed: boolean; dismissed: boolean };
}

interface SessionState {
	standalone?: boolean;
	scope?: string;
	paused?: boolean;
	tourId: string;
	stepIndex: number;
	tourNeighborId: number | null;
}

const SESSION_KEY = "walkthrough_active_tour";
const TRANSITION_MS = 250;

export default class extends Controller {
	static targets = ["toggleBtn", "panel", "tourList", "invitation", "invitationError"];
	static values = {
		tours: Array,
		chapterIds: Array,
		csrf: String,
		scope: String
	};

	declare readonly toggleBtnTarget: HTMLElement;
	declare readonly hasToggleBtnTarget: boolean;
	declare readonly panelTarget: HTMLElement;
	declare readonly hasPanelTarget: boolean;
	declare readonly tourListTarget: HTMLElement;
	declare readonly hasTourListTarget: boolean;
	declare readonly toursValue: TourSummary[];
	declare readonly csrfValue: string;
	declare readonly scopeValue: string;
	declare readonly chapterIdsValue: string[];
	private surface!: WalkthroughSurface;
	private boundReposition = () => this.reposition();
	private layoutObserver: ResizeObserver | null = null;

	declare readonly invitationTarget: HTMLElement;
	declare readonly hasInvitationTarget: boolean;
	declare readonly invitationErrorTarget: HTMLElement;
	private invitationBusy = false;

	private activeTour: TourData | null = null;
	private currentStepIndex: number = 0;
	private tourNeighborId: number | null = null;
	private tooltipEl: HTMLElement | null = null;
	private backdropEl: HTMLElement | null = null;
	private highlightedEl: HTMLElement | null = null;
	private panelOpen: boolean = false;
	private transitioning: boolean = false;

	private boundStartTourFromEvent = (e: Event) =>
		this.startTourFromEvent(e as CustomEvent);

	connect() {
		// Skip during Turbo's cached page preview to avoid double-rendering
		if (document.documentElement.hasAttribute("data-turbo-preview")) return;

		window.addEventListener(
			"walkthrough:start-tour",
			this.boundStartTourFromEvent,
		);

		this.surface = new WalkthroughSurface(this.application);
		document.addEventListener("scroll", this.boundReposition, true);
		document.addEventListener("transitionend", this.boundReposition, true);
		window.addEventListener("resize", this.boundReposition);
		window.visualViewport?.addEventListener("resize", this.boundReposition);
		this.renderTourList();
		this.resumeFromSession();

		if (!this.getSessionState() && this.hasInvitationTarget) {
			this.invitationTarget.hidden = false;
			this.tooltipEl = this.invitationTarget;
			this.positionInvitation();
			requestAnimationFrame(() => this.tooltipEl?.classList.add("is-visible"));
		}
	}

	disconnect() {
		window.removeEventListener(
			"walkthrough:start-tour",
			this.boundStartTourFromEvent,
		);
		document.removeEventListener("scroll", this.boundReposition, true);
		document.removeEventListener("transitionend", this.boundReposition, true);
		window.removeEventListener("resize", this.boundReposition);
		window.visualViewport?.removeEventListener("resize", this.boundReposition);
		this.surface?.close();
		this.activeTour = null;
		this.cleanup();
	}

	positionInvitation() {
		if (!this.hasInvitationTarget || this.invitationTarget.hidden) return;
		const anchor = this.findTourElement(this.invitationTarget.dataset.tourAnchor || "")
			|| (this.hasToggleBtnTarget ? this.toggleBtnTarget : null);
		if (anchor) this.positionTooltip(this.invitationTarget, anchor, anchor === this.toggleBtnTarget ? "top" : "bottom");
	}

	acceptInvitation() {
		return this.respondToInvitation(true);
	}

	skipInvitation() {
		return this.respondToInvitation(false);
	}

	private async respondToInvitation(start: boolean) {
		if (!this.hasInvitationTarget || this.invitationTarget.hidden || this.invitationBusy) return;
		const prompt = this.invitationTarget;
		const tourId = prompt.dataset.tourId!;
		this.invitationBusy = true;
		this.invitationErrorTarget.hidden = true;
		prompt.setAttribute("aria-busy", "true");
		prompt.querySelectorAll<HTMLButtonElement>("button").forEach(button => button.disabled = true);
		const succeeded = start ? await this.loadAndStartTour(tourId, 0, true) : await this.updateState(tourId, "dismiss");
		if (!succeeded && this.hasInvitationTarget) this.invitationErrorTarget.hidden = false;
		if (succeeded && !start) await this.fadeOutAll();
		prompt.removeAttribute("aria-busy");
		prompt.querySelectorAll<HTMLButtonElement>("button").forEach(button => button.disabled = false);
		this.invitationBusy = false;
		if (succeeded) ahoy.track(start ? "module_guide_started" : "module_guide_dismissed");
	}

	escape() {
		if (this.activeTour) this.closeTour();
		else this.skipInvitation();
	}

	openLibrary() {
		this.closePanel();
		window.dispatchEvent(new CustomEvent("walkthrough:open-library"));
	}

	hideLauncher() {
		if (this.hasToggleBtnTarget) this.toggleBtnTarget.hidden = true;
		this.closePanel();
	}

	private reposition() {
		if (this.tooltipEl && this.highlightedEl) {
			const step = this.activeTour?.steps[this.currentStepIndex];
			this.positionTooltip(this.tooltipEl, this.highlightedEl, step?.side || "bottom");
		}
	}

	// --- Panel ---

	togglePanel() {
		if (!this.hasPanelTarget) return;

		if (this.panelOpen) {
			this.panelOpen = false;
			this.panelTarget.classList.remove("is-open");
		} else {
			this.panelOpen = true;
			this.panelTarget.classList.add("is-open");
		}
	}

	private closePanel() {
		this.panelOpen = false;
		if (this.hasPanelTarget) this.panelTarget.classList.remove("is-open");
	}

	// --- Tour List ---

	private renderTourList() {
		if (!this.hasTourListTarget) return;

		const tours = this.toursValue.filter(tour => tour.id === "getting_started");
		if (!tours || tours.length === 0) {
			this.tourListTarget.innerHTML =
				`<p class="text-sm text-base-content/60">${i18n.t('walkthrough.no_tours')}</p>`;
			return;
		}

		this.tourListTarget.innerHTML = tours
			.map((tour) => {
				const isCompleted = tour.completed;
				const btnLabel = isCompleted ? i18n.t('walkthrough.restart') : i18n.t('walkthrough.start');
				const btnClass = isCompleted
					? "btn btn-ghost btn-xs"
					: "btn btn-primary btn-xs";
				const statusBadge = isCompleted
					? `<span class="badge badge-success badge-xs">${i18n.t('walkthrough.completed')}</span>`
					: "";

				return `
        <div class="flex items-center justify-between py-2 border-b last:border-b-0">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium truncate">${i18n.t('product_guides.basics')}</span>
              ${statusBadge}
            </div>
            <p class="text-xs text-base-content/60 truncate">${tour.description}</p>
          </div>
          <button class="${btnClass} ml-2 shrink-0" data-action="walkthrough#startTour" data-tour-id="${tour.id}">
            ${btnLabel}
          </button>
        </div>
      `;
			})
			.join("");
	}

	// --- Start / Resume Tour ---

	private async startTourFromEvent(event: CustomEvent) {
		const tourId = event.detail?.tourId;
		if (!tourId) return;

		const saved = this.getSessionState();
		const resume = event.detail.resume && saved?.paused && saved.tourId === tourId;
		this.tourNeighborId = resume ? saved.tourNeighborId : null;
		this.closePanel();
		this.surface?.close();
		await this.fadeOutAll();
		await this.loadAndStartTour(tourId, resume ? saved.stepIndex : 0);
	}

	async startTour(event: Event) {
		const button = event.currentTarget as HTMLElement;
		const tourId = button.dataset.tourId;
		if (!tourId) return;

		this.tourNeighborId = null;
		this.closePanel();
		await this.fadeOutAll();

		// Reset if restarting
		const tour = this.toursValue.find((tourSummary) => tourSummary.id === tourId);
		if (tour?.completed) {
			await this.updateState(tourId, "reset");
		}

		await this.loadAndStartTour(tourId);
	}

	private async loadAndStartTour(tourId: string, startIndex: number = 0, standalone = false) {
		try {
			const chapter = this.chapterIdsValue.includes(tourId) || this.toursValue.some(tour => tour.id === tourId && tour.collection === "deep_dive");
			const response = await fetch(`/walkthrough/${tourId}${chapter ? "/start" : ""}`, {
				method: chapter ? "POST" : "GET",
				headers: { Accept: "application/json", "X-CSRF-Token": this.csrfValue }
			});

			if (!this.element.isConnected) return false;
			if (!response.ok) throw new Error("Guide unavailable");

			const data: TourData = await response.json();
			if (!data.steps.length) return false;
			if (data.context === "neighbor") {
				const match = window.location.pathname.match(/^\/neighbors\/(\d+)(?:\/|$)/);
				this.tourNeighborId = match ? Number(match[1]) : this.tourNeighborId;
				if (!this.tourNeighborId) {
					this.clearSessionState();
					Turbo.visit("/neighbors");
					return false;
				}
			}
			this.activeTour = { ...data, standalone };
			window.dispatchEvent(new CustomEvent("walkthrough:started", { detail: { tourId } }));
			this.currentStepIndex = Math.min(startIndex, data.steps.length - 1);

			await this.showStep();
			return true;
		} catch (e) {
			console.error("Failed to load tour:", e);
			this.activeTour = null;
			this.clearSessionState();
			this.surface?.close();
			if (!this.hasInvitationTarget || this.invitationTarget.hidden) {
				this.cleanup();
				if (this.element.isConnected) window.dispatchEvent(new CustomEvent("walkthrough:failed"));
			}
			return false;
		}
	}

	private resumeFromSession() {
		const session = this.getSessionState();
		if (!session || session.paused) return;

		this.tourNeighborId = session.tourNeighborId;
		this.loadAndStartTour(session.tourId, session.stepIndex, session.standalone);
	}

	// --- Step Display ---

	private async showStep() {
		if (!this.activeTour || this.transitioning) return;

		const step = this.activeTour.steps[this.currentStepIndex];
		if (!step) return;

		// Close side menu if this step doesn't need it open
		if (!step.open_menu) {
			this.closeSideMenu();
		}

		this.saveSessionState();

		const resolvedPage = this.resolvePagePattern(step.page);

		if (!this.matchesCurrentPage(resolvedPage)) {
			await this.fadeOutAll();
			this.navigateToStep(step);
			return;
		}

		if (this.activeTour.collection === "deep_dive") {
			await this.fadeOutAll();
			if (!this.activeTour || !this.element.isConnected) return;
			this.transitioning = true;
			try {
				await this.surface.prepare(step);
			} catch {
				if (this.activeTour && this.element.isConnected) {
					this.closeTour();
					window.dispatchEvent(new CustomEvent("walkthrough:failed"));
				}
				return;
			} finally { this.transitioning = false; }
			if (!this.activeTour || !this.element.isConnected) return;
		}
		const positionChanges = this.willChangePosition(step);

		if (positionChanges) {
			await this.fadeOutAll();
			if (this.activeTour && this.element.isConnected) this.renderStep(step);
		} else {
			this.updateStepContent(step);
		}
	}

	private renderStep(step: TourStep) {
		if (step.open_menu) {
			this.openSideMenu();
			// Wait for menu slide-in transition before finding elements inside it
			setTimeout(() => this.renderStepContent(step), TRANSITION_MS);
			return;
		}

		this.renderStepContent(step);
	}

	private renderStepContent(step: TourStep) {
		if (step.type === "modal") {
			this.showModalStep(step);
			return;
		}

		if (step.element || step.selector) {
			const el = this.stepTarget(step);
			if (el) {
				this.highlightElement(el);
				this.showTooltip(step, el);
				return;
			}
		}

		this.showModalStep(step);
	}

	private updateStepContent(step: TourStep) {
		if (step.type === "modal" || (!step.element && !step.selector)) {
			this.updateModalContent(step);
			return;
		}

		const el = this.stepTarget(step);
		if (el && this.tooltipEl) {
			this.updateTooltipContent(step, el);
		} else {
			this.updateModalContent(step);
		}
	}

	private stepTarget(step: TourStep) {
		return step.highlight ? this.surface.target(step.highlight) : step.selector ? this.surface.target(step.selector) : step.element ? this.findTourElement(step.element) : null;
	}

	private willChangePosition(nextStep: TourStep): boolean {
		const hasExisting = !!(this.tooltipEl || this.backdropEl);
		if (!hasExisting) return true;

		const currentIsModal =
			this.backdropEl?.classList.contains("walkthrough-modal-backdrop") ??
			false;
		const nextElement = this.stepTarget(nextStep);
		const nextIsModal = nextStep.type === "modal" || !nextElement;

		if (currentIsModal !== nextIsModal) return true;
		if (currentIsModal && nextIsModal) return false;

		return this.highlightedEl !== nextElement;
	}

	private async navigateToStep(step: TourStep) {
		let url = step.page;

		if (url.includes(":id") || url.includes(":neighbor_id")) {
			if (!this.tourNeighborId && this.activeTour?.demo_neighbor) {
				await this.fetchTourNeighbor();
			}
			if (this.tourNeighborId) {
				url = url
					.replace(":neighbor_id", String(this.tourNeighborId))
					.replace(":id", String(this.tourNeighborId));
			} else {
				console.error("Could not resolve tour neighbor URL:", url);
				return;
			}
		}

		this.saveSessionState();
		Turbo.visit(url);
	}

	private async fetchTourNeighbor() {
		try {
			const response = await fetch("/walkthrough/tour_neighbor", {
				method: "POST",
				headers: {
					Accept: "application/json",
					"X-CSRF-Token": this.csrfValue
				}
			});
			if (response.ok) {
				const data = await response.json();
				this.tourNeighborId = data.neighbor_id;
			}
		} catch (e) {
			console.error("Failed to fetch tour neighbor:", e);
		}
	}

	// --- Highlighting ---

	private highlightElement(el: HTMLElement) {
		this.addBackdrop();
		el.scrollIntoView({ behavior: "instant", block: "center" });
		el.classList.add("walkthrough-highlight");
		this.highlightedEl = el;
		this.layoutObserver?.disconnect();
		this.layoutObserver = new ResizeObserver(this.boundReposition);
		this.layoutObserver.observe(el);
	}

	private removeHighlight() {
		this.layoutObserver?.disconnect();
		this.layoutObserver = null;
		if (this.highlightedEl) {
			this.highlightedEl.classList.remove("walkthrough-highlight");
			this.highlightedEl = null;
		}
	}

	private addBackdrop() {
		this.destroyBackdropEl();
		const backdrop = document.createElement("div");
		backdrop.className = "walkthrough-backdrop";
		this.element.appendChild(backdrop);
		this.backdropEl = backdrop;
		requestAnimationFrame(() => backdrop.classList.add("is-visible"));
	}

	private destroyBackdropEl() {
		if (this.backdropEl) {
			this.backdropEl.remove();
			this.backdropEl = null;
		}
	}

	// --- Tooltip ---

	private completionLabel() {
		const chapters = this.toursValue.filter(tour => tour.collection === "deep_dive");
		const index = chapters.findIndex(tour => tour.id === this.activeTour?.id);
		return !this.activeTour?.standalone && index >= 0 && index < chapters.length - 1
			? i18n.t('product_guides.continue_next') : i18n.t('walkthrough.finish');
	}

	async chooseChapter() {
		await this.closeTour();
		window.dispatchEvent(new CustomEvent("walkthrough:open-library"));
	}

	private showTooltip(step: TourStep, targetEl: HTMLElement) {
		this.destroyTooltipEl();

		const totalSteps = this.activeTour!.steps.length;
		const stepNum = this.currentStepIndex + 1;
		const isFirst = this.currentStepIndex === 0;
		const isLast = this.currentStepIndex === totalSteps - 1;

		const helpBtn = step.help_article
			? `<a href="/help/articles/${step.help_article}" class="btn btn-ghost btn-xs" target="_blank">${i18n.t('walkthrough.help')}</a>`
			: "";
		const descriptionExtra = step.help_article
			? i18n.t('walkthrough.help_learn_more_html')
			: "";

		const tooltip = document.createElement("div");
		tooltip.className = "walkthrough-tooltip card bg-base-100 shadow-xl border";
		tooltip.innerHTML = `
      <div class="card-body p-4">
        <div class="flex items-center justify-between">
          <h4 class="font-semibold text-sm">${step.title}</h4>
          <button class="btn btn-ghost btn-xs btn-circle" data-action="click->walkthrough#closeTour">
            &times;
          </button>
        </div>
        ${this.activeTour?.collection === "deep_dive" && !this.activeTour.standalone ? `<button class="text-xs text-primary text-left" data-action="click->walkthrough#chooseChapter">${i18n.t('product_guides.choose_chapter')}</button>` : ""}
        <p class="text-sm text-base-content/70 mt-1">${step.description}${descriptionExtra ? `<br/><br/>${descriptionExtra}` : ""}</p>
        <div class="flex items-center justify-between mt-3">
          <span class="text-xs text-base-content/50">${i18n.t('walkthrough.step_of', { current: stepNum, total: totalSteps })}</span>
          <div class="flex gap-1">
            ${helpBtn}
            ${!isFirst ? `<button class="btn btn-ghost btn-xs" data-action="click->walkthrough#prevStep">${i18n.t('walkthrough.back')}</button>` : ""}
            <button class="btn btn-primary btn-xs" data-action="click->walkthrough#nextStep">
              ${isLast ? this.completionLabel() : i18n.t('walkthrough.next')}
            </button>
          </div>
        </div>
      </div>
    `;

		this.element.appendChild(tooltip);
		this.tooltipEl = tooltip;
		this.layoutObserver?.observe(tooltip);

		this.positionTooltip(tooltip, targetEl, step.side || "bottom");

		requestAnimationFrame(() => tooltip.classList.add("is-visible"));
	}

	private showModalStep(step: TourStep) {
		this.destroyTooltipEl();
		this.destroyBackdropEl();

		const totalSteps = this.activeTour!.steps.length;
		const stepNum = this.currentStepIndex + 1;
		const isFirst = this.currentStepIndex === 0;
		const isLast = this.currentStepIndex === totalSteps - 1;

		const helpBtn = step.help_article
			? `<a href="/help/articles/${step.help_article}" class="btn btn-ghost btn-sm" target="_blank">${i18n.t('walkthrough.help')}</a>`
			: "";
		const descriptionExtra = step.help_article
			? i18n.t('walkthrough.help_learn_more_html')
			: "";

		const backdrop = document.createElement("div");
		backdrop.className = "walkthrough-modal-backdrop";
		backdrop.innerHTML = `
      <div class="card bg-base-100 shadow-xl border w-96 max-w-[calc(100vw-2rem)]">
        <div class="card-body p-6 text-center">
          <h3 class="font-bold text-lg">${step.title}</h3>
          <p class="text-sm text-base-content/70 mt-2">${step.description}${descriptionExtra ? `<br/><br/>${descriptionExtra}` : ""}</p>
          <div class="flex items-center justify-between mt-4">
            <span class="text-xs text-base-content/50">${i18n.t('walkthrough.step_of', { current: stepNum, total: totalSteps })}</span>
            <div class="flex gap-2">
              ${helpBtn}
              ${!isFirst ? `<button class="btn btn-ghost btn-sm" data-action="click->walkthrough#prevStep">${i18n.t('walkthrough.back')}</button>` : `<button class="btn btn-ghost btn-sm" data-action="click->walkthrough#closeTour">${i18n.t('walkthrough.maybe_later')}</button>`}
              <button class="btn btn-primary btn-sm" data-action="click->walkthrough#nextStep">
                ${isLast ? this.completionLabel() : isFirst ? i18n.t('walkthrough.start_tour') : i18n.t('walkthrough.next')}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

		this.element.appendChild(backdrop);
		this.backdropEl = backdrop;

		requestAnimationFrame(() => backdrop.classList.add("is-visible"));

		if (step.confetti) {
			setTimeout(() => this.showConfetti(), 300);
		}
	}

	private updateModalContent(step: TourStep) {
		if (!this.backdropEl) return;

		const cardBody = this.backdropEl.querySelector(".card-body") as HTMLElement;
		if (!cardBody) return;

		const totalSteps = this.activeTour!.steps.length;
		const stepNum = this.currentStepIndex + 1;
		const isFirst = this.currentStepIndex === 0;
		const isLast = this.currentStepIndex === totalSteps - 1;

		const helpBtn = step.help_article
			? `<a href="/help/articles/${step.help_article}" class="btn btn-ghost btn-sm" target="_blank">${i18n.t('walkthrough.help')}</a>`
			: "";
		const descriptionExtra = step.help_article
			? i18n.t('walkthrough.help_learn_more_html')
			: "";

		const newContent = `
      <h3 class="font-bold text-lg">${step.title}</h3>
      <p class="text-sm text-base-content/70 mt-2">${step.description}${descriptionExtra ? `<br/><br/>${descriptionExtra}` : ""}</p>
      <div class="flex items-center justify-between mt-4">
        <span class="text-xs text-base-content/50">${i18n.t('walkthrough.step_of', { current: stepNum, total: totalSteps })}</span>
        <div class="flex gap-2">
          ${helpBtn}
          ${!isFirst ? `<button class="btn btn-ghost btn-sm" data-action="click->walkthrough#prevStep">${i18n.t('walkthrough.back')}</button>` : `<button class="btn btn-ghost btn-sm" data-action="click->walkthrough#closeTour">${i18n.t('walkthrough.maybe_later')}</button>`}
          <button class="btn btn-primary btn-sm" data-action="click->walkthrough#nextStep">
            ${isLast ? this.completionLabel() : isFirst ? i18n.t('walkthrough.start_tour') : i18n.t('walkthrough.next')}
          </button>
        </div>
      </div>
    `;

		// Fade out, swap content, fade in
		this.transitioning = true;
		cardBody.classList.add("is-swapping");
		setTimeout(() => {
			cardBody.innerHTML = newContent;
			cardBody.classList.remove("is-swapping");
			this.transitioning = false;
		}, 200);
	}

	private updateTooltipContent(step: TourStep, targetEl: HTMLElement) {
		if (!this.tooltipEl) return;

		const totalSteps = this.activeTour!.steps.length;
		const stepNum = this.currentStepIndex + 1;
		const isFirst = this.currentStepIndex === 0;
		const isLast = this.currentStepIndex === totalSteps - 1;

		const helpBtn = step.help_article
			? `<a href="/help/articles/${step.help_article}" class="btn btn-ghost btn-xs" target="_blank">${i18n.t('walkthrough.help')}</a>`
			: "";
		const descriptionExtra = step.help_article
			? i18n.t('walkthrough.help_learn_more_html')
			: "";

		const cardBody = this.tooltipEl.querySelector(".card-body");
		if (!cardBody) return;

		cardBody.innerHTML = `
      <div class="flex items-center justify-between">
        <h4 class="font-semibold text-sm">${step.title}</h4>
        <button class="btn btn-ghost btn-xs btn-circle" data-action="click->walkthrough#closeTour">
          &times;
        </button>
      </div>
      ${this.activeTour?.collection === "deep_dive" && !this.activeTour.standalone ? `<button class="text-xs text-primary text-left" data-action="click->walkthrough#chooseChapter">${i18n.t('product_guides.choose_chapter')}</button>` : ""}
      <p class="text-sm text-base-content/70 mt-1">${step.description}${descriptionExtra ? `<br/><br/>${descriptionExtra}` : ""}</p>
      <div class="flex items-center justify-between mt-3">
        <span class="text-xs text-base-content/50">${i18n.t('walkthrough.step_of', { current: stepNum, total: totalSteps })}</span>
        <div class="flex gap-1">
          ${helpBtn}
          ${!isFirst ? `<button class="btn btn-ghost btn-xs" data-action="click->walkthrough#prevStep">${i18n.t('walkthrough.back')}</button>` : ""}
          <button class="btn btn-primary btn-xs" data-action="click->walkthrough#nextStep">
            ${isLast ? this.completionLabel() : i18n.t('walkthrough.next')}
          </button>
        </div>
      </div>
    `;

		this.positionTooltip(this.tooltipEl, targetEl, step.side || "bottom");
	}

	private positionTooltip(
		tooltip: HTMLElement,
		target: HTMLElement,
		side: string,
	) {
		const rect = target.getBoundingClientRect();
		if (this.backdropEl?.classList.contains("walkthrough-backdrop")) {
			const { top, right, bottom, left } = rect;
			this.backdropEl.style.clipPath = `polygon(evenodd, 0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${left - 4}px ${top - 4}px, ${left - 4}px ${bottom + 4}px, ${right + 4}px ${bottom + 4}px, ${right + 4}px ${top - 4}px, ${left - 4}px ${top - 4}px)`;
		}
		if (this.activeTour?.collection === "deep_dive") {
			// Keep navigation independent of drawer motion and changing target bounds.
			const inset = window.innerWidth >= 768 ? 24 : 8;
			tooltip.style.left = `${window.innerWidth >= 768 ? inset : (window.innerWidth - tooltip.offsetWidth) / 2}px`;
			tooltip.style.top = "auto";
			tooltip.style.bottom = `${side === "top" ? Math.max(inset, window.innerHeight - rect.top + 12) : inset}px`;
			return;
		}
		const gap = 12;
		// Keep desktop explanations beside drawers, outside the highlighted fields.
		if (side === "left" && rect.left < tooltip.offsetWidth + gap + 8) {
			side = rect.top >= tooltip.offsetHeight + gap + 8 ? "top" : "bottom";
		}

		let top: number;
		let left: number;

		switch (side) {
			case "top":
				top = rect.top - tooltip.offsetHeight - gap;
				left = rect.left + rect.width / 2 - tooltip.offsetWidth / 2;
				break;
			case "left":
				top = rect.top + rect.height / 2 - tooltip.offsetHeight / 2;
				left = rect.left - tooltip.offsetWidth - gap;
				break;
			case "right":
				top = rect.top + rect.height / 2 - tooltip.offsetHeight / 2;
				left = rect.right + gap;
				break;
			default: // bottom
				top = rect.bottom + gap;
				left = rect.left + rect.width / 2 - tooltip.offsetWidth / 2;
				break;
		}

		// Clamp to viewport
		const padding = 8;
		const viewportHeight = window.visualViewport?.height || window.innerHeight;
		left = Math.max(
			padding,
			Math.min(left, window.innerWidth - tooltip.offsetWidth - padding),
		);
		top = Math.max(
			padding,
			Math.min(top, viewportHeight - tooltip.offsetHeight - padding),
		);

		tooltip.style.top = `${top}px`;
		tooltip.style.left = `${left}px`;
	}

	private destroyTooltipEl() {
		if (this.tooltipEl) {
			this.tooltipEl.remove();
			this.tooltipEl = null;
		}
	}

	// --- Navigation ---

	nextStep() {
		if (!this.activeTour || this.transitioning) return;

		const nextIndex = this.currentStepIndex + 1;

		if (nextIndex >= this.activeTour.steps.length) {
			this.completeTour();
			return;
		}

		this.currentStepIndex = nextIndex;
		this.showStep();
	}

	prevStep() {
		if (!this.activeTour || this.transitioning || this.currentStepIndex <= 0) return;

		this.currentStepIndex -= 1;
		this.showStep();
	}

	closeTour() {
		const tourId = this.activeTour?.id;
		const chapter = this.activeTour?.collection === "deep_dive";
		if (chapter) this.saveSessionState(true);
		this.surface?.close();
		this.activeTour = null;
		this.tourNeighborId = null;
		if (!chapter) this.clearSessionState();
		this.closeSideMenu();
		return this.fadeOutAll().then(() => {
			if (tourId) return this.updateState(tourId, "dismiss");
		});
	}

	private async completeTour() {
		if (!this.activeTour) return;

		const tour = this.activeTour;
		if (!await this.updateState(tour.id, "complete")) {
			window.dispatchEvent(new CustomEvent("walkthrough:failed"));
			return;
		}
		this.surface?.close();
		this.activeTour = null;
		this.tourNeighborId = null;
		this.clearSessionState();
		this.closeSideMenu();
		await this.fadeOutAll();
		if (tour.collection === "deep_dive" && !tour.standalone) window.dispatchEvent(new CustomEvent("walkthrough:completed", { detail: { tourId: tour.id } }));
	}

	// --- Animated teardown ---

	private fadeOutAll(): Promise<void> {
		const hasElements = !!(
			this.tooltipEl ||
			this.backdropEl ||
			this.highlightedEl
		);

		if (!hasElements) return Promise.resolve();

		this.transitioning = true;

		// Add leaving class to trigger CSS exit transitions
		if (this.tooltipEl) this.tooltipEl.classList.add("is-leaving");
		if (this.backdropEl) this.backdropEl.classList.add("is-leaving");

		// Remove highlight immediately (it fades via its own transition)
		this.removeHighlight();

		return new Promise((resolve) => {
			setTimeout(() => {
				this.destroyTooltipEl();
				this.destroyBackdropEl();
				this.transitioning = false;
				resolve();
			}, TRANSITION_MS);
		});
	}

	// --- State Persistence ---

	private async updateState(tourId: string, actionType: string) {
		try {
			const response = await fetch(`/walkthrough/${tourId}`, {
				method: "PATCH",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					"X-CSRF-Token": this.csrfValue
				},
				body: JSON.stringify({ action_type: actionType })
			});
			if (!response.ok) return false;
			const state = await response.json();
			if (!state.launcher_visible) this.hideLauncher();
			return true;
		} catch (e) {
			console.error("Failed to update walkthrough state:", e);
			return false;
		}
	}

	private saveSessionState(paused = false) {
		if (!this.activeTour) return;

		const state: SessionState = {
			scope: this.scopeValue,
			standalone: this.activeTour.standalone,
			paused,
			tourId: this.activeTour.id,
			stepIndex: this.currentStepIndex,
			tourNeighborId: this.tourNeighborId
		};
		sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
	}

	private getSessionState(): SessionState | null {
		const raw = sessionStorage.getItem(SESSION_KEY);
		if (!raw) return null;

		try {
			const saved = JSON.parse(raw) as SessionState;
			if (this.scopeValue && saved.scope !== this.scopeValue) {
				this.clearSessionState();
				return null;
			}
			return saved;
		} catch {
			return null;
		}
	}

	private clearSessionState() {
		sessionStorage.removeItem(SESSION_KEY);
	}


	// --- Page Matching ---

	private resolvePagePattern(page: string): string {
		let resolved = page;
		if (this.tourNeighborId) {
			resolved = resolved
				.replace(":neighbor_id", String(this.tourNeighborId))
				.replace(":id", String(this.tourNeighborId));
		}
		return resolved;
	}

	private matchesCurrentPage(pattern: string): boolean {
		const currentPath = window.location.pathname;
		// Compare paths only. A step's query string (e.g. ?open=demo) is applied when
		// navigating, but including it here would make the page look perpetually
		// unmatched — re-navigating on every step and looping.
		const patternPath = pattern.split("?")[0];
		if (this.activeTour?.collection === "deep_dive" && pattern.includes("?")) {
			const expected = new URL(pattern, window.location.origin);
			const actual = new URL(window.location.href);
			return currentPath === expected.pathname && [...expected.searchParams].every(([key, value]) => actual.searchParams.get(key) === value);
		}

		if (patternPath.includes(":")) {
			const regexStr = patternPath.replace(/:[a-z_]+/g, "[^/]+");
			return new RegExp(`^${regexStr}$`).test(currentPath);
		}

		return currentPath === patternPath || (this.activeTour?.collection !== "deep_dive" && currentPath.startsWith(patternPath + "/"));
	}

	// --- Element Resolution ---

	private findTourElement(tourTarget: string): HTMLElement | null {
		const selector = `[data-tour="${tourTarget}"]`;
		const candidates = document.querySelectorAll<HTMLElement>(selector);

		for (const el of candidates) {
			if (el.checkVisibility({ visibilityProperty: true, opacityProperty: true })) {
				return el;
			}
		}

		return null;
	}

	// --- Side Menu ---

	private openSideMenu() {
		this.navigationMenu()?.open();
	}

	private closeSideMenu() {
		this.navigationMenu()?.close();
	}

	private navigationMenu() {
		const mobileMenu = document.querySelector<HTMLElement>('[data-controller~="mobile-menu"]');
		const identifier = mobileMenu && window.innerWidth < 768 ? "mobile-menu" : "side-menu";
		const element = document.querySelector<HTMLElement>(`[data-controller~="${identifier}"]`);
		return element && this.application.getControllerForElementAndIdentifier(element, identifier) as (Controller & { open(): void; close(): void }) | null;
	}

	// --- Confetti ---

	private showConfetti() {
		const defaults = { zIndex: 56, disableForReducedMotion: true };

		// Burst from top-left
		confetti({ ...defaults, angle: 60, spread: 120, origin: { x: 0, y: 0.3 }, particleCount: 80, startVelocity: 55, gravity: 0.8 });

		// Burst from mid-right
		setTimeout(() => {
			confetti({ ...defaults, angle: 120, spread: 120, origin: { x: 1, y: 0.5 }, particleCount: 80, startVelocity: 55, gravity: 0.8 });
		}, 150);

		// Burst from bottom-left
		setTimeout(() => {
			confetti({ ...defaults, angle: 60, spread: 120, origin: { x: 0, y: .9 }, particleCount: 80, startVelocity: 55, gravity: 0.8 });
		}, 300);
	}

	// --- Cleanup (instant, for disconnect) ---

	private cleanup() {
		this.removeHighlight();
		this.destroyTooltipEl();
		this.destroyBackdropEl();
	}
}
